/* El catálogo de herramientas del tablero.
 *
 * ── UNA FOTO POR TIPO, NO POR HERRAMIENTA ───────────────────────────────────
 *
 * Igna, 14/09, con una lámina de 22 herramientas recortadas: *«el catálogo lo
 * quiero súper realista»*. Tenía razón y los dibujos vectoriales no llegaban:
 * lo que dibujo son formas planas, y la lámina es fotografía.
 *
 * Lo que NO cambia es la regla que lo empezó todo: **no se pide una foto por
 * herramienta**. La foto es del TIPO —un taladro— y la comparten las cinco
 * unidades que haya. Nadie saca fotos; el alta sigue siendo elegir de una
 * grilla.
 *
 * Van en `assets/` y no en un bucket: son 27 archivos de 256px, 856 KB en
 * total, que cambian cuando cambia el código y no cuando alguien sube algo. Se
 * versionan con el repo, se sirven cacheados, y no hay un camino de subida que
 * mantener ni permisos que revisar.
 *
 * De la primera lámina salieron 21. Los seis que faltaban —torx, llave
 * combinada suelta, criquet, soldadora de estaño, amoladora y guantes— los
 * mandó Igna en una segunda lámina el 14/09 y se recortaron igual. Importa
 * porque las cinco herramientas que estaban cargadas en el tablero ese día
 * caían todas en esa lista: el pañol real se veía dibujado y el resto del
 * catálogo fotografiado.
 *
 * ── LO QUE NO TIENE FOTO IGUAL SE DIBUJA ────────────────────────────────────
 *
 * Queda un solo tipo sin foto, `generica`, y no es un pendiente: es el dibujo
 * que le toca a una herramienta cuyo tipo no está en el catálogo. Para ése
 * sigue el vector — un lugar vacío en la pared se lee como «acá no falta
 * nada», que es lo contrario de lo que pasa.
 *
 * ── POR QUÉ NO SON FOTOS *DE CADA HERRAMIENTA* ──────────────────────────────
 *
 * Igna, 14/09/2026: *«No les vamos a pedir foto. Es carente de sentido. Un
 * taladro es un taladro, lo mismo con llaves y etc. no nos importa si es marca
 * DeWalt o blah»*.
 *
 * El tablero arrancó pidiendo una foto por herramienta y una silueta aparte.
 * Eso convertía cada alta en una sesión de fotos: sacarla, recortarla, subirla
 * — y mientras tanto la herramienta no se podía usar en el tablero, o entraba
 * con un rectángulo punteado que no dice nada. Treinta herramientas son
 * treinta fotos que nadie va a sacar.
 *
 * Y no hacía falta: lo que el tablero tiene que responder en dos segundos es
 * QUÉ FALTA, y para eso alcanza con que la forma se reconozca. La marca del
 * taladro no cambia la respuesta.
 *
 * ── YA NO HAY SILUETA, Y POR QUÉ ────────────────────────────────────────────
 *
 * Hasta el 14/09 la herramienta prestada se pintaba de un solo tono oscuro:
 * el hueco en la pared. Igna la sacó ese mismo día: *«no hay 1 de cada cosa,
 * por lo que que se ponga en gris no tiene sentido. Pero sí la etiqueta de
 * quién lo tiene y hace cuánto y desde cuándo»*.
 *
 * Tenía razón y el error era de modelo, no de dibujo: el hueco supone UN
 * tablero físico con UNA herramienta por gancho. Con tres llaves combinadas,
 * apagar «la» posición porque salió una dice algo falso —la llave está, hay
 * dos más—. Lo que cuenta el estado de verdad es la chapa, que es un dato.
 *
 * Los colores igual NO se escriben adentro de cada `d`: cada parte declara de
 * qué ES (metal, mango, cuerpo…) y el componente decide con qué pintarla. Eso
 * ya no es por la silueta —es para tener un solo lugar donde cambiar la
 * paleta— y sigue valiendo.
 *
 * ── LA CLAVE ES EL CONCEPTO ─────────────────────────────────────────────────
 *
 * `taladro`, no `taladro-dewalt-20v`. La fila de `taller_herramienta` guarda
 * esa clave en `tipo`; el dibujo se elige de acá. Cambiar mañana cómo se
 * dibuja un taladro no obliga a tocar ninguna fila.
 *
 * Todos en una grilla de 100×100, dibujados en su orientación natural y
 * ocupando la caja. Es lo que hace que la pared se lea como una pared y no
 * como una bolsa de iconos de distintos tamaños.
 */

import f_amoladora from "../assets/herramientas/amoladora.png";
import f_cinta_metrica from "../assets/herramientas/cinta_metrica.png";
import f_cutter from "../assets/herramientas/cutter.png";
import f_destornillador_phillips from "../assets/herramientas/destornillador_phillips.png";
import f_destornillador_plano from "../assets/herramientas/destornillador_plano.png";
import f_destornillador_torx from "../assets/herramientas/destornillador_torx.png";
import f_espatula from "../assets/herramientas/espatula.png";
import f_espatula_angosta from "../assets/herramientas/espatula_angosta.png";
import f_formon from "../assets/herramientas/formon.png";
import f_guantes from "../assets/herramientas/guantes.png";
import f_juego_llaves_combinadas from "../assets/herramientas/juego_llaves_combinadas.png";
import f_llave_combinada from "../assets/herramientas/llave_combinada.png";
import f_llave_francesa from "../assets/herramientas/llave_francesa.png";
import f_llave_tubo from "../assets/herramientas/llave_tubo.png";
import f_llaves_allen from "../assets/herramientas/llaves_allen.png";
import f_martillo from "../assets/herramientas/martillo.png";
import f_martillo_bola from "../assets/herramientas/martillo_bola.png";
import f_maza from "../assets/herramientas/maza.png";
import f_nivel from "../assets/herramientas/nivel.png";
import f_pinza_pico_loro from "../assets/herramientas/pinza_pico_loro.png";
import f_pinza_presion from "../assets/herramientas/pinza_presion.png";
import f_pinza_universal from "../assets/herramientas/pinza_universal.png";
import f_sierra from "../assets/herramientas/sierra.png";
import f_soldadora_estano from "../assets/herramientas/soldadora_estano.png";
import f_taladro from "../assets/herramientas/taladro.png";
import f_taladro_cable from "../assets/herramientas/taladro_cable.png";
import f_tijera_chapa from "../assets/herramientas/tijera_chapa.png";

/* De qué está hecha cada parte. Son roles, no colores: el componente los
   traduce, y así la paleta se cambia en un solo lugar. */
export const MATERIALES = ["metal", "metal_oscuro", "mango", "mango_2",
                           "cuerpo", "acento", "vidrio"];

/* Un mango de destornillador, que se repite en tres. Se arma con una función
   para que los tres sean idénticos y cambien juntos. */
const mangoRecto = (c = "mango") => ([
  { d: "M40 2h20a10 10 0 0 1 10 10v26a10 10 0 0 1-10 10H40a10 10 0 0 1-10-10V12a10 10 0 0 1 10-10z", c },
  { d: "M31 16h38v5H31zM31 28h38v5H31z", c: "mango_2" },
]);

/* El vástago y la punta: largos. Un destornillador con el mango más grande que
   la hoja se lee como un tapón. */
const vastago = { d: "M45 48h10v38H45z", c: "metal" };

export const DIBUJOS = {
  destornillador_plano: {
    nombre: "Destornillador plano",
    categoria: "Manuales",
    foto: f_destornillador_plano,
    partes: [...mangoRecto(), vastago, { d: "M42 86h16v10H42z", c: "metal_oscuro" }],
  },

  destornillador_phillips: {
    nombre: "Destornillador Phillips",
    categoria: "Manuales",
    foto: f_destornillador_phillips,
    partes: [...mangoRecto("acento"), vastago,
             { d: "M43 86h14v4l-5 6h-4l-5-6z", c: "metal_oscuro" }],
  },

  destornillador_torx: {
    nombre: "Destornillador Torx",
    categoria: "Manuales",
    foto: f_destornillador_torx,
    partes: [...mangoRecto("mango_2"), vastago,
             { d: "M44 86h12l3 5-3 5h-12l-3-5z", c: "metal_oscuro" }],
  },

  llave_combinada: {
    nombre: "Llave combinada",
    categoria: "Llaves",
    foto: f_llave_combinada,
    partes: [
      // Boca abierta arriba, anillo abajo, caña en el medio.
      { d: "M38 6h24v16h-8v-8h-8v8h-8z", c: "metal" },
      { d: "M44 20h12v54H44z", c: "metal" },
      { d: "M50 68a14 14 0 1 1 0 28 14 14 0 0 1 0-28zm0 8a6 6 0 1 0 0 12 6 6 0 0 0 0-12z", c: "metal" },
    ],
  },

  juego_llaves_combinadas: {
    nombre: "Juego de llaves combinadas",
    categoria: "Llaves",
    foto: f_juego_llaves_combinadas,
    partes: [
      { d: "M10 10h16v10h-5v-5h-6v5h-5zM14 18h8v64h-8zM18 74a11 11 0 1 1 0 22 11 11 0 0 1 0-22zm0 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z", c: "metal" },
      { d: "M32 12h16v10h-5v-5h-6v5h-5zM36 20h8v60h-8zM40 72a11 11 0 1 1 0 22 11 11 0 0 1 0-22zm0 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z", c: "metal" },
      { d: "M54 14h16v10h-5v-5h-6v5h-5zM58 22h8v56h-8zM62 70a11 11 0 1 1 0 22 11 11 0 0 1 0-22zm0 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z", c: "metal" },
      { d: "M76 16h16v10h-5v-5h-6v5h-5zM80 24h8v52h-8zM84 68a11 11 0 1 1 0 22 11 11 0 0 1 0-22zm0 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z", c: "metal" },
    ],
  },

  llave_francesa: {
    nombre: "Llave francesa",
    categoria: "Llaves",
    foto: f_llave_francesa,
    partes: [
      // Quijada fija (larga) y quijada móvil (corta): esa diferencia de altura
      // es lo único que distingue una francesa de una llave fija.
      { d: "M26 4h16v24H26z", c: "metal" },
      { d: "M52 13h16v15H52z", c: "metal" },
      { d: "M24 28h44v16H24z", c: "metal" },
      { d: "M31 32h30v8H31z", c: "metal_oscuro" },
      { d: "M40 44h14v50a7 7 0 0 1-14 0z", c: "metal" },
      { d: "M42 58h10v28H42z", c: "metal_oscuro" },
    ],
  },

  pinza_universal: {
    nombre: "Pinza universal",
    categoria: "Pinzas",
    foto: f_pinza_universal,
    partes: [
      { d: "M41 4h18l5 28H36z", c: "metal" },
      { d: "M49 4h2v28h-2z", c: "metal_oscuro" },
      { d: "M31 32h38v12H31z", c: "metal" },
      { d: "M34 44l-5 50h15l3-50zM66 44l5 50H56l-3-50z", c: "mango" },
      { d: "M32 56h14v6H32zM54 56h14v6H54z", c: "mango_2" },
    ],
  },

  pinza_pico_loro: {
    nombre: "Pinza pico de loro",
    categoria: "Pinzas",
    foto: f_pinza_pico_loro,
    partes: [
      // La cabeza corrida a un lado y la ranura larga del cuello son lo que la
      // separan de la universal. Se parecen porque son parientes: el nombre de
      // la chapa termina de decir cuál es.
      { d: "M24 4h14l6 18h16v8H24z", c: "metal" },
      { d: "M28 30h32v10H28z", c: "metal" },
      { d: "M44 21h18v7H44z", c: "metal_oscuro" },
      { d: "M34 40l-6 54h15l3-54zM58 40l8 54H53l-4-54z", c: "acento" },
      { d: "M31 58h15v6H31zM55 58h15v6H55z", c: "mango_2" },
    ],
  },

  pinza_presion: {
    nombre: "Pinza de presión",
    categoria: "Pinzas",
    foto: f_pinza_presion,
    partes: [
      { d: "M30 4h16v14h14V4h12v26H30z", c: "metal" },
      { d: "M32 30h34v12H32z", c: "metal" },
      { d: "M40 42h13v52h-13z", c: "metal" },
      // El mango de abajo se abre y termina en el tornillo de regulación: es
      // lo que distingue una morsa de mano de una pinza común.
      { d: "M55 46h13v30l-13 18z", c: "metal_oscuro" },
      { d: "M64 84h12v10H64z", c: "metal" },
    ],
  },

  martillo: {
    nombre: "Martillo",
    categoria: "Golpe",
    foto: f_martillo,
    partes: [
      { d: "M18 8h26v24H18z", c: "metal_oscuro" },
      { d: "M44 10c14 0 20 6 22 10 2 4-2 12-10 12H44z", c: "metal_oscuro" },
      { d: "M38 30h14v64H38z", c: "mango" },
    ],
  },

  maza: {
    nombre: "Maza",
    categoria: "Golpe",
    foto: f_maza,
    partes: [
      { d: "M14 8h72v26H14z", c: "metal_oscuro" },
      { d: "M42 34h16v60H42z", c: "mango" },
    ],
  },

  taladro: {
    nombre: "Taladro",
    categoria: "Eléctricas",
    foto: f_taladro,
    partes: [
      { d: "M22 20h44a10 10 0 0 1 10 10v16a10 10 0 0 1-10 10H22z", c: "cuerpo" },
      { d: "M76 30h8v16h-8z", c: "metal_oscuro" },
      { d: "M84 34h12v8H84z", c: "metal" },
      { d: "M30 56h22l-4 20H34z", c: "cuerpo" },
      { d: "M24 76h36v18H24z", c: "acento" },
      { d: "M22 44h10v10H22z", c: "metal_oscuro" },
    ],
  },

  amoladora: {
    nombre: "Amoladora",
    categoria: "Eléctricas",
    foto: f_amoladora,
    partes: [
      { d: "M6 38h48v24H6z", c: "cuerpo" },
      { d: "M54 32h16v36H54z", c: "metal_oscuro" },
      { d: "M62 18h8a24 24 0 0 1 0 64h-8z", c: "metal" },
      { d: "M70 26a24 24 0 1 1 0 48 24 24 0 0 1 0-48z", c: "metal_oscuro" },
      { d: "M14 62h26v8H14z", c: "acento" },
    ],
  },

  soldadora_estano: {
    nombre: "Soldadora de estaño",
    categoria: "Eléctricas",
    foto: f_soldadora_estano,
    partes: [
      { d: "M18 34h44v26H18z", c: "cuerpo" },
      { d: "M62 40h14v14H62z", c: "metal_oscuro" },
      { d: "M76 44h10l8 3-8 3h-10z", c: "acento" },
      { d: "M6 40h12v14H6z", c: "metal_oscuro" },
      { d: "M26 62h20v8H26z", c: "acento" },
    ],
  },

  llaves_allen: {
    nombre: "Llaves Allen",
    categoria: "Llaves",
    foto: f_llaves_allen,
    partes: [
      { d: "M12 22h10v50H12zM12 62h22v10H12z", c: "metal" },
      { d: "M30 16h10v56H30zM30 62h24v10H30z", c: "metal" },
      { d: "M48 22h10v50H48zM48 62h26v10H48z", c: "metal" },
      { d: "M66 30h10v42H66zM66 62h24v10H66z", c: "metal" },
    ],
  },

  cinta_metrica: {
    nombre: "Cinta métrica",
    categoria: "Medición",
    foto: f_cinta_metrica,
    partes: [
      { d: "M14 22h56a14 14 0 0 1 14 14v30a14 14 0 0 1-14 14H14a14 14 0 0 1-14-14V36a14 14 0 0 1 14-14z", c: "acento" },
      { d: "M24 38h36v24H24z", c: "cuerpo" },
      { d: "M84 46h12v10H84z", c: "metal" },
      { d: "M94 42h5v18h-5z", c: "metal_oscuro" },
    ],
  },

  cutter: {
    nombre: "Cutter",
    categoria: "Corte",
    foto: f_cutter,
    partes: [
      { d: "M6 40h68v20H6z", c: "acento" },
      { d: "M16 46h34v8H16z", c: "cuerpo" },
      { d: "M74 42h10l16 12-16 4h-10z", c: "metal" },
    ],
  },

  nivel: {
    nombre: "Nivel",
    categoria: "Medición",
    foto: f_nivel,
    partes: [
      { d: "M2 38h96v24H2z", c: "acento" },
      { d: "M12 44h24v12H12zM64 44h24v12H64z", c: "cuerpo" },
      { d: "M40 42h20v16H40z", c: "vidrio" },
    ],
  },

  sierra: {
    nombre: "Sierra de arco",
    categoria: "Corte",
    foto: f_sierra,
    partes: [
      { d: "M14 14h72v10H14zM76 14h10v52H76z", c: "acento" },
      { d: "M14 14h10v52H14z", c: "acento" },
      { d: "M14 62h72v8H14z", c: "metal" },
      { d: "M14 70h72l-4 6h-64z", c: "metal_oscuro" },
      { d: "M2 56h16v30H2z", c: "cuerpo" },
    ],
  },

  tijera_chapa: {
    nombre: "Tijera de chapa",
    categoria: "Corte",
    foto: f_tijera_chapa,
    partes: [
      { d: "M40 4h9l7 30H43z", c: "metal" },
      { d: "M51 4h9l-3 30H44z", c: "metal" },
      { d: "M44 32h12v10H44z", c: "metal_oscuro" },
      { d: "M40 42l-14 52h16l6-52zM60 42l14 52H58l-6-52z", c: "mango" },
      { d: "M32 74h14v7H32zM54 74h14v7H54z", c: "mango_2" },
    ],
  },

  llave_tubo: {
    nombre: "Llave tubo / criquet",
    categoria: "Llaves",
    foto: f_llave_tubo,
    partes: [
      { d: "M50 4a18 18 0 1 1 0 36 18 18 0 0 1 0-36z", c: "metal" },
      { d: "M44 16h12v12H44z", c: "metal_oscuro" },
      { d: "M40 38h20v10H40z", c: "metal" },
      { d: "M43 48h14v46a7 7 0 0 1-14 0z", c: "metal" },
      { d: "M43 58h14v30H43z", c: "mango_2" },
    ],
  },

  martillo_bola: { nombre: "Martillo de bola", categoria: "Golpe", foto: f_martillo_bola },
  taladro_cable: { nombre: "Taladro con cable", categoria: "Eléctricas", foto: f_taladro_cable },
  formon: { nombre: "Formón", categoria: "Corte", foto: f_formon },
  espatula: { nombre: "Espátula", categoria: "Corte", foto: f_espatula },
  espatula_angosta: { nombre: "Espátula angosta", categoria: "Corte", foto: f_espatula_angosta },

  guantes: {
    nombre: "Guantes",
    categoria: "Protección",
    foto: f_guantes,
    partes: [
      // No es una herramienta, y va igual: Nico cargó «Guantes» el primer día.
      // Lo que el tablero contesta es qué falta del pañol, y un par de guantes
      // que no está es exactamente eso.
      { d: "M36 29a5 5 0 0 1 10 0v33H36z", c: "acento" },
      { d: "M47 21a5 5 0 0 1 10 0v41H47z", c: "acento" },
      { d: "M58 25a5 5 0 0 1 10 0v37H58z", c: "acento" },
      { d: "M69 33a5 5 0 0 1 10 0v29H69z", c: "acento" },
      { d: "M19 50a7 7 0 0 1 11-4l10 14-10 8-11-14z", c: "acento" },
      { d: "M34 54h45v16a14 14 0 0 1-14 14H48a14 14 0 0 1-14-14z", c: "acento" },
      { d: "M36 82h42v14H36z", c: "cuerpo" },
    ],
  },

  generica: {
    nombre: "Herramienta (genérica)",
    categoria: "Otras",
    partes: [
      // Una llave fija en diagonal: es la forma con la que se dibuja
      // «herramienta» en todos lados, y ninguna herramienta real se le parece
      // tanto como para confundirla con otra cosa.
      { d: "M62 6a26 26 0 0 0-24 34L12 66l14 14 26-26a26 26 0 0 0 34-24l-18 8-12-6-2-12z", c: "metal" },
      { d: "M18 62l12 12-6 6-12-12z", c: "metal_oscuro" },
    ],
  },
};

export const TIPOS = Object.keys(DIBUJOS);

/* Las categorías en el orden en que se muestran en el selector. Sale de los
   dibujos y no de una lista aparte: una lista aparte se desincroniza el día
   que alguien agregue un dibujo con una categoría nueva. */
export function categorias() {
  const vistas = [];
  for (const t of TIPOS) {
    const c = DIBUJOS[t].categoria;
    if (!vistas.includes(c)) vistas.push(c);
  }
  return vistas;
}

/* El dibujo de un tipo. NUNCA null: una herramienta cargada con un tipo que ya
   no existe igual tiene que ocupar su lugar en la pared — un hueco vacío se
   lee como «no falta nada ahí», que es lo contrario de lo que pasa. */
export function dibujoDe(tipo) {
  return DIBUJOS[String(tipo || "")] || DIBUJOS.generica;
}

/* La foto del tipo, o "" si todavía no tiene. Lo que no tiene foto se dibuja:
   ver `partesDe`. */
export function fotoDe(tipo) {
  return dibujoDe(tipo).foto || "";
}

/* Las partes vectoriales con las que se dibuja. Un tipo con foto no las tiene,
   y uno sin foto ni partes —uno nuevo al que se le olvidó el dibujo— cae en la
   genérica: un lugar vacío en la pared se lee como «acá no falta nada». */
export function partesDe(tipo) {
  return dibujoDe(tipo).partes || DIBUJOS.generica.partes;
}

export function nombreDeTipo(tipo) {
  return dibujoDe(tipo).nombre;
}

/* Qué tipo proponer para un nombre escrito a mano. Es una AYUDA del alta: si
   no acierta, la persona elige de la lista. Por eso ante la duda no elige —
   devolver `generica` es honesto; elegir «martillo» porque el nombre dice
   «martillo neumático» sería dibujar otra cosa. */
const PISTAS = [
  ["destornillador_phillips", ["phillips", "cruz", "philips"]],
  ["destornillador_torx", ["torx", "estrella"]],
  ["destornillador_plano", ["destornillador", "desarmador"]],
  ["juego_llaves_combinadas", ["juego de llaves", "juego llaves"]],
  ["llave_francesa", ["francesa", "ajustable"]],
  ["llave_tubo", ["tubo", "criquet", "crique", "ratchet"]],
  ["llaves_allen", ["allen", "hexagonal"]],
  ["llave_combinada", ["combinada", "llave"]],
  ["pinza_pico_loro", ["pico de loro", "pico loro"]],
  ["pinza_presion", ["presion", "presión", "morsa"]],
  ["pinza_universal", ["pinza"]],
  ["maza", ["maza", "mazo"]],
  ["martillo_bola", ["bola", "bolita"]],
  ["martillo", ["martillo"]],
  ["taladro_cable", ["taladro con cable", "taladro de cable", "percutor"]],
  ["taladro", ["taladro", "atornillador"]],
  ["amoladora", ["amoladora", "esmeril"]],
  ["soldadora_estano", ["soldador", "estano", "estaño"]],
  ["cinta_metrica", ["cinta metrica", "cinta métrica", "metro"]],
  ["cutter", ["cutter", "trincheta"]],
  ["nivel", ["nivel"]],
  ["sierra", ["sierra", "serrucho", "arco"]],
  ["formon", ["formon", "formón", "escoplo"]],
  ["espatula_angosta", ["espatula angosta", "espátula angosta", "emplaste"]],
  ["espatula", ["espatula", "espátula", "rasqueta"]],
  ["tijera_chapa", ["tijera"]],
  ["guantes", ["guante"]],
];

export function tipoSugerido(nombre) {
  const t = String(nombre || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  for (const [tipo, pistas] of PISTAS) {
    if (pistas.some((p) => t.includes(p.normalize("NFD").replace(/[̀-ͯ]/g, "")))) {
      return tipo;
    }
  }
  return "generica";
}
